/**
 * Cell seeding arithmetic.
 *
 * Cells per vessel = target density (cells/cm²) × growth area (cm²), or the per-vessel
 * number typed directly. The seeding mix is made for N × (1 + overage) vessels so the last
 * well is not short: its cells come from the counted suspension (cells ÷ concentration)
 * and the rest of its volume is fresh medium.
 *
 * Vessel areas and the starting densities below are Thermo Fisher Scientific's published
 * "Useful numbers for cell culture" table (Gibco Cell Culture Basics); seeding densities
 * there were measured with HeLa cells. Other makers' vessels differ slightly, so the
 * area can always be overridden.
 */

export type VesselId =
  | "dish35"
  | "dish60"
  | "dish100"
  | "dish150"
  | "plate6"
  | "plate12"
  | "plate24"
  | "plate48"
  | "plate96"
  | "plate384"
  | "plate1536"
  | "flaskT25"
  | "flaskT75"
  | "flaskT175"
  | "flaskT225";

export interface Vessel {
  id: VesselId;
  /** Growth area of one dish, well or flask (cm²). */
  areaCm2: number;
  /** Thermo's suggested seeding number per dish, well or flask. */
  startingCells: number;
  /** Suggested growth medium per vessel (mL): [min, max]. */
  mediumMl: readonly [number, number];
}

export const VESSELS: readonly Vessel[] = [
  { id: "dish35", areaCm2: 8.8, startingCells: 300_000, mediumMl: [2, 2] },
  { id: "dish60", areaCm2: 21.5, startingCells: 800_000, mediumMl: [5, 5] },
  {
    id: "dish100",
    areaCm2: 56.7,
    startingCells: 2_200_000,
    mediumMl: [12, 12],
  },
  { id: "dish150", areaCm2: 145, startingCells: 5_000_000, mediumMl: [30, 30] },
  { id: "plate6", areaCm2: 9.6, startingCells: 300_000, mediumMl: [1, 3] },
  { id: "plate12", areaCm2: 3.5, startingCells: 100_000, mediumMl: [1, 2] },
  { id: "plate24", areaCm2: 1.9, startingCells: 50_000, mediumMl: [0.5, 1] },
  { id: "plate48", areaCm2: 1.1, startingCells: 30_000, mediumMl: [0.2, 0.4] },
  { id: "plate96", areaCm2: 0.32, startingCells: 10_000, mediumMl: [0.1, 0.2] },
  {
    id: "plate384",
    areaCm2: 0.084,
    startingCells: 1800,
    mediumMl: [0.08, 0.08],
  },
  {
    id: "plate1536",
    areaCm2: 0.03,
    startingCells: 900,
    mediumMl: [0.01, 0.01],
  },
  { id: "flaskT25", areaCm2: 25, startingCells: 700_000, mediumMl: [3, 5] },
  { id: "flaskT75", areaCm2: 75, startingCells: 2_100_000, mediumMl: [8, 15] },
  {
    id: "flaskT175",
    areaCm2: 175,
    startingCells: 4_900_000,
    mediumMl: [35, 53],
  },
  {
    id: "flaskT225",
    areaCm2: 225,
    startingCells: 6_300_000,
    mediumMl: [45, 68],
  },
];

export function vesselById(id: VesselId): Vessel {
  return VESSELS.find((v) => v.id === id) ?? VESSELS[0];
}

export type SeedMode = "perCm2" | "perVessel";

export type SeedError =
  | "area"
  | "count"
  | "target"
  | "concentration"
  | "medium"
  | "overage";

export interface SeedInput {
  areaCm2: number;
  count: number;
  mode: SeedMode;
  target: number;
  /** Cells per mL of the counted suspension. */
  concentration: number;
  /** Total volume of medium plus cells in each vessel (mL). */
  mediumMl: number;
  overagePct: number;
}

export interface SeedPlan {
  cellsPerVessel: number;
  cellsPerCm2: number;
  /** Suspension to add to one vessel (mL). */
  suspensionPerVesselMl: number;
  effectiveVessels: number;
  totalCells: number;
  mixVolumeMl: number;
  suspensionMl: number;
  freshMediumMl: number;
  cellsPerMlInMix: number;
  /** The counted suspension is too dilute to reach this density in that volume. */
  tooDilute: boolean;
}

export function planSeeding(
  input: SeedInput
): { plan: SeedPlan } | { error: SeedError } {
  if (!(input.areaCm2 > 0)) {
    return { error: "area" };
  }
  if (!(input.count >= 1)) {
    return { error: "count" };
  }
  if (!(input.target > 0)) {
    return { error: "target" };
  }
  if (!(input.concentration > 0)) {
    return { error: "concentration" };
  }
  if (!(input.mediumMl > 0)) {
    return { error: "medium" };
  }
  if (!(input.overagePct >= 0)) {
    return { error: "overage" };
  }
  const cellsPerVessel =
    input.mode === "perCm2" ? input.target * input.areaCm2 : input.target;
  const effective = input.count * (1 + input.overagePct / 100);
  const totalCells = cellsPerVessel * effective;
  const mixVolumeMl = input.mediumMl * effective;
  const suspensionMl = totalCells / input.concentration;
  return {
    plan: {
      cellsPerVessel,
      cellsPerCm2: cellsPerVessel / input.areaCm2,
      suspensionPerVesselMl: cellsPerVessel / input.concentration,
      effectiveVessels: effective,
      totalCells,
      mixVolumeMl,
      suspensionMl,
      freshMediumMl: mixVolumeMl - suspensionMl,
      cellsPerMlInMix: totalCells / mixVolumeMl,
      tooDilute: suspensionMl > mixVolumeMl,
    },
  };
}
